import React, { useState, useEffect, useCallback } from 'react';
import { useSession } from '@/context/SessionContext';
import LearnerLayout from '@/components/LearnerLayout';
import { Input } from '@/components/ui/input';
import { Loader2, FolderOpen, FileText, Download, ChevronRight, Search, Home, File, Image, FileType } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getFileIcon = (name) => {
  if (!name) return FileText;
  const ext = name.split('.').pop()?.toLowerCase();
  if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext)) return Image;
  if (['pdf'].includes(ext)) return FileType;
  return File;
};

const LearnerDocumentsPage = () => {
  const { selectedSessionId, currentCourse } = useSession();
  const [folders, setFolders] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  // Navigation: breadcrumb path of folder objects
  const [breadcrumb, setBreadcrumb] = useState([]);
  const [currentFolderId, setCurrentFolderId] = useState(null);
  const [folderContents, setFolderContents] = useState(null);
  const [folderLoading, setFolderLoading] = useState(false);

  // Initial load
  useEffect(() => {
    if (!selectedSessionId) { setLoading(false); return; }
    fetch(`${API}/learner/sessions/${selectedSessionId}/documents`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : { folders: [], documents: [] })
      .then(data => {
        setFolders(data.folders || []);
        setDocuments(data.documents || []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [selectedSessionId]);

  // Navigate into a folder
  const openFolder = useCallback(async (folder) => {
    setFolderLoading(true);
    setCurrentFolderId(folder.folder_id);
    setBreadcrumb(prev => [...prev, folder]);
    try {
      const res = await fetch(
        `${API}/learner/sessions/${selectedSessionId}/folders/${folder.folder_id}/contents`,
        { credentials: 'include' }
      );
      if (res.ok) {
        setFolderContents(await res.json());
      }
    } catch (e) { /* silent */ }
    setFolderLoading(false);
  }, [selectedSessionId]);

  // Go back to root
  const goToRoot = () => {
    setBreadcrumb([]);
    setCurrentFolderId(null);
    setFolderContents(null);
  };

  // Go to a specific breadcrumb level
  const goToBreadcrumb = (index) => {
    if (index < 0) return goToRoot();
    const target = breadcrumb[index];
    setBreadcrumb(prev => prev.slice(0, index + 1));
    setCurrentFolderId(target.folder_id);
    // Re-fetch that folder
    setFolderLoading(true);
    fetch(`${API}/learner/sessions/${selectedSessionId}/folders/${target.folder_id}/contents`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setFolderContents(d); })
      .catch(() => {})
      .finally(() => setFolderLoading(false));
  };

  const courseTitle = currentCourse?.title || 'Formation';
  const nonRootFolders = folders.filter(f => f.folder_id !== 'default');
  const adminDocs = documents.filter(d => d.folder_id === 'admin' || d.content_type?.includes('pdf'));

  // Count docs per folder
  const docCountForFolder = (folderId) => documents.filter(d => d.folder_id === folderId).length;

  const filteredFolders = search
    ? nonRootFolders.filter(f => f.name.toLowerCase().includes(search.toLowerCase()))
    : nonRootFolders;

  // Folder detail view
  if (currentFolderId && folderContents) {
    const subFolders = folderContents.sub_folders || [];
    const folderDocs = folderContents.documents || [];
    const currentFolder = folderContents.folder;

    // Also include docs from the main documents list that match this folder
    const mainDocs = documents.filter(d => d.folder_id === currentFolderId);
    const allDocs = [...folderDocs];
    mainDocs.forEach(md => {
      if (!allDocs.find(d => d.doc_id === md.doc_id)) allDocs.push(md);
    });

    return (
      <LearnerLayout>
        <div data-testid="learner-documents-page">
          <p className="text-sm text-slate-400 mb-4">{courseTitle} - Mes documents</p>

          {/* Breadcrumb */}
          <div className="flex items-center gap-1.5 text-sm mb-6 flex-wrap" data-testid="breadcrumb">
            <button onClick={goToRoot}
              className="text-blue-600 hover:text-blue-800 flex items-center gap-1 font-medium"
              data-testid="breadcrumb-root">
              <Home className="h-3.5 w-3.5" />Documents
            </button>
            {breadcrumb.map((bc, i) => (
              <React.Fragment key={bc.folder_id}>
                <ChevronRight className="h-3.5 w-3.5 text-slate-300" />
                {i === breadcrumb.length - 1 ? (
                  <span className="text-slate-700 font-medium">{bc.name}</span>
                ) : (
                  <button onClick={() => goToBreadcrumb(i)}
                    className="text-blue-600 hover:text-blue-800 font-medium"
                    data-testid={`breadcrumb-${i}`}>
                    {bc.name}
                  </button>
                )}
              </React.Fragment>
            ))}
          </div>

          <h2 className="text-2xl font-bold text-slate-900 mb-6">{currentFolder?.name || 'Dossier'}</h2>

          {folderLoading ? (
            <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-blue-600" /></div>
          ) : (
            <div className="space-y-6">
              {/* Sub-folders */}
              {subFolders.length > 0 && (
                <div className="flex flex-wrap gap-6" data-testid="sub-folders">
                  {subFolders.map(sf => (
                    <button key={sf.folder_id}
                      className="flex flex-col items-center w-28 group cursor-pointer"
                      onClick={() => openFolder(sf)}
                      data-testid={`subfolder-${sf.folder_id}`}>
                      <div className="w-20 h-20 rounded-full bg-blue-700 flex items-center justify-center mb-3 group-hover:bg-blue-800 transition-colors shadow-lg">
                        <FolderOpen className="h-9 w-9 text-white" />
                      </div>
                      <span className="text-xs text-slate-700 text-center leading-snug font-medium uppercase">{sf.name}</span>
                    </button>
                  ))}
                </div>
              )}

              {/* Documents */}
              {allDocs.length > 0 ? (
                <div className="border border-slate-200 rounded-2xl overflow-hidden" data-testid="folder-documents">
                  {allDocs.map((doc, i) => {
                    const Icon = getFileIcon(doc.original_name);
                    return (
                      <div key={doc.doc_id}
                        className={`flex items-center justify-between px-6 py-4 hover:bg-slate-50 transition-colors ${i > 0 ? 'border-t border-slate-100' : ''}`}
                        data-testid={`doc-${doc.doc_id}`}>
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center shrink-0">
                            <Icon className="h-5 w-5 text-blue-600" />
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-slate-900 truncate">{doc.original_name}</p>
                            <p className="text-[10px] text-slate-400">
                              {doc.size ? `${(doc.size / 1024).toFixed(0)} Ko` : ''}
                              {doc.uploaded_at ? ` · ${new Date(doc.uploaded_at).toLocaleDateString('fr-FR')}` : ''}
                            </p>
                          </div>
                        </div>
                        <button className="text-blue-600 hover:text-blue-800 p-2 rounded-lg hover:bg-blue-50 transition-colors"
                          onClick={() => window.open(`${API}/admin/sessions/${selectedSessionId}/documents/${doc.doc_id}/download`, '_blank')}
                          data-testid={`download-${doc.doc_id}`}>
                          <Download className="h-4 w-4" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              ) : subFolders.length === 0 ? (
                <div className="text-center py-12">
                  <FileText className="h-10 w-10 mx-auto text-slate-300 mb-2" />
                  <p className="text-slate-400 italic text-sm">Ce dossier est vide.</p>
                </div>
              ) : null}
            </div>
          )}
        </div>
      </LearnerLayout>
    );
  }

  // Root view
  return (
    <LearnerLayout>
      <div data-testid="learner-documents-page">
        <p className="text-sm text-slate-400 mb-6">{courseTitle} - Mes documents</p>
        <h1 className="text-4xl md:text-5xl font-extrabold text-slate-900 mb-10 italic" data-testid="documents-title">Mes documents</h1>

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-blue-600" /></div>
        ) : (
          <div className="space-y-8">
            {/* Search bar */}
            <div className="relative" data-testid="doc-search">
              <Input
                placeholder="Rechercher"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="h-12 text-base border-0 border-b border-slate-200 rounded-none px-0 focus-visible:ring-0 focus-visible:border-blue-500"
              />
            </div>

            {/* Documents administratifs */}
            <div className="border border-slate-200 rounded-2xl p-6" data-testid="admin-docs-section">
              <div className="flex items-center gap-3 mb-4">
                <FolderOpen className="h-5 w-5 text-slate-500" />
                <h2 className="text-lg font-semibold text-slate-900">Documents administratifs</h2>
                <span className="text-xs text-slate-400">({adminDocs.length})</span>
              </div>
              {adminDocs.length > 0 ? (
                <div className="space-y-1">
                  {adminDocs.map((doc) => {
                    const Icon = getFileIcon(doc.original_name);
                    return (
                      <div key={doc.doc_id} className="flex items-center justify-between py-2.5 px-3 rounded-lg hover:bg-slate-50 transition-colors">
                        <div className="flex items-center gap-3">
                          <Icon className="h-4 w-4 text-blue-600" />
                          <span className="text-sm text-slate-700">{doc.original_name}</span>
                          {doc.size && <span className="text-[10px] text-slate-400">{(doc.size / 1024).toFixed(0)} Ko</span>}
                        </div>
                        <button className="text-blue-600 hover:text-blue-800 p-1"
                          onClick={() => window.open(`${API}/admin/sessions/${selectedSessionId}/documents/${doc.doc_id}/download`, '_blank')}>
                          <Download className="h-4 w-4" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-slate-400 italic text-sm" data-testid="no-admin-docs">Il n'y a aucun document administratif.</p>
              )}
            </div>

            {/* Dossier racine */}
            <div className="border border-slate-200 rounded-2xl p-6" data-testid="root-folder-section">
              <div className="flex items-center gap-3 mb-8">
                <FolderOpen className="h-5 w-5 text-slate-500" />
                <h2 className="text-lg font-semibold text-slate-900">Dossier racine</h2>
                <span className="text-xs text-slate-400">({filteredFolders.length} dossiers)</span>
              </div>

              {filteredFolders.length > 0 ? (
                <div className="flex flex-wrap gap-8" data-testid="folder-icons-grid">
                  {filteredFolders.map((folder) => {
                    const count = docCountForFolder(folder.folder_id);
                    return (
                      <button
                        key={folder.folder_id}
                        className="flex flex-col items-center w-28 group cursor-pointer"
                        onClick={() => openFolder(folder)}
                        data-testid={`folder-icon-${folder.folder_id}`}
                      >
                        <div className="relative">
                          <div className="w-20 h-20 rounded-full bg-blue-700 flex items-center justify-center mb-3 group-hover:bg-blue-800 transition-colors shadow-lg">
                            <FolderOpen className="h-9 w-9 text-white" />
                          </div>
                          {count > 0 && (
                            <span className="absolute -top-1 -right-1 w-6 h-6 rounded-full bg-violet-500 text-white text-[10px] font-bold flex items-center justify-center shadow" data-testid={`folder-count-${folder.folder_id}`}>
                              {count}
                            </span>
                          )}
                        </div>
                        <span className="text-xs text-slate-700 text-center leading-snug font-medium uppercase">{folder.name}</span>
                      </button>
                    );
                  })}
                </div>
              ) : (
                <p className="text-slate-400 italic text-sm">Aucun dossier disponible.</p>
              )}
            </div>
          </div>
        )}
      </div>
    </LearnerLayout>
  );
};

export default LearnerDocumentsPage;
